import { useMutation } from "@apollo/client/react";
import { IconButton, Stack, Typography, styled } from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { EditableTextField } from "../../components";
import type { SaveEmailRecipientMutation, SaveEmailRecipientMutationVariables } from "../../graphql/types";
import { Recipient, SaveEmailRecipientDocument } from "../../graphql/types";

import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { PencilIcon } from "@phosphor-icons/react/dist/csr/Pencil";
import { TrashIcon } from "@phosphor-icons/react/dist/csr/Trash";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { DeactivateRecipientWarningModal } from "./DeactivateRecipientWarningModal";
import { validateEmail } from "./utils";

type EmailTitleProps = {
  email: string;
  showDeactivateRecipientModal: boolean;
  closeDeactivateRecipientModal: () => void;
  openDeactivateRecipientModal: () => void;
};

type DefaultValuesFormType = {
  email: string;
};

const Form = styled("form")`
  width: 100%;
  flex-direction: column;
  display: flex;
`;

const EmailHeader = ({
  email,
  closeDeactivateRecipientModal,
  openDeactivateRecipientModal,
  showDeactivateRecipientModal,
}: EmailTitleProps) => {
  const domain = email.split("@")[1];

  const validateEmailEdit = (value: string) => {
    if (value === email) return false;
    return validateEmail(value);
  };

  const [editingRecipient, setEditingRecipient] = useState(false);

  const {
    control,
    formState: { isValid },
    handleSubmit,
    resetField,
    setError,
  } = useForm<DefaultValuesFormType>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      email,
    },
  });

  const [saveEmailRecipient, { loading }] = useMutation<
    SaveEmailRecipientMutation,
    SaveEmailRecipientMutationVariables
  >(SaveEmailRecipientDocument, {
    onCompleted: (data) => {
      if (data?.save_emailrecipient?.__typename === "SaveEmailRecipientOk") {
        if (!data?.save_emailrecipient) return;
        const { newEmail } = data.save_emailrecipient;

        if (newEmail) {
          toast.success("Recipient updated successfully");
          setEditingRecipient(false);
        } else {
          toast.success("Recipient deleted successfully");
        }
      } else if (data?.save_emailrecipient?.__typename === "RecipientAlreadyExistsError") {
        setError("email", {
          message: "The introduced email already exists.",
        });
        toast.error("Error updating recipient");
      }
    },
    update: (cache, { data }) => {
      if (data?.save_emailrecipient?.__typename === "SaveEmailRecipientOk") {
        if (!data?.save_emailrecipient) return;
        const { newEmail, oldEmail } = data.save_emailrecipient;
        if (newEmail) {
          cache.modify({
            fields: {
              recipients(existingRecipients = []) {
                return existingRecipients.map((recipient: Recipient) => {
                  if (recipient.email === oldEmail) {
                    return {
                      ...recipient,
                      email: newEmail,
                    };
                  }
                  return recipient;
                });
              },
            },
          });
        } else {
          cache.modify({
            fields: {
              recipients(existingRecipients = []) {
                return existingRecipients.filter((recipient: Recipient) => {
                  return recipient.email !== oldEmail;
                });
              },
            },
          });
        }
      }
    },
  });

  const onDeleteRecipient = () => {
    saveEmailRecipient({
      variables: {
        recipient: email,
        deactivate: true,
      },
    });
  };

  const onUpdateRecipient = async (formData: DefaultValuesFormType) => {
    if (loading) return;
    return saveEmailRecipient({
      variables: {
        recipient: email,
        newRecipient: formData.email,
      },
    });
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (loading) return;
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        handleSubmit(onUpdateRecipient)();
        break;
      case "Escape":
        setEditingRecipient(false);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Stack direction="row" sx={{ width: "100%", justifyContent: "space-between" }}>
        <Form onSubmit={handleSubmit(onUpdateRecipient)}>
          <EditableTextField
            name="email"
            textFieldWidth={editingRecipient ? "70%" : "100%"}
            textFieldVariant="outlined"
            control={control}
            rules={{
              required: "Email is required",
              validate: validateEmailEdit,
            }}
            inputProps={{ "aria-label": `Type to edit recipient ${email}` }}
            splitTextCharacter={editingRecipient ? undefined : "@"}
            isLoading={loading}
            onKeyDown={handleKeyDown}
            onFocus={() => setEditingRecipient(true)}
            seamless={!editingRecipient}
            controls={
              editingRecipient ? (
                <>
                  <IconButton
                    onClick={() => {
                      setEditingRecipient(false);
                      resetField("email");
                    }}
                  >
                    <XIcon />
                  </IconButton>
                  <IconButton
                    disabled={!isValid || loading}
                    type="submit"
                    aria-label={`Confirm edition of recipient ${email}`}
                  >
                    <CheckIcon />
                  </IconButton>
                </>
              ) : (
                <IconButton onClick={() => setEditingRecipient(true)} aria-label={`Edit the recipient ${email}`}>
                  <PencilIcon />
                </IconButton>
              )
            }
          />
          {!editingRecipient && (
            <Typography variant="body.medium.regular" sx={{ color: "text30.main" }}>{`@${domain}`}</Typography>
          )}
        </Form>
        <IconButton onClick={openDeactivateRecipientModal} aria-label={`Delete recipient ${email}`}>
          <TrashIcon />
        </IconButton>
      </Stack>
      <DeactivateRecipientWarningModal
        open={showDeactivateRecipientModal}
        onCancel={closeDeactivateRecipientModal}
        onDeactivate={onDeleteRecipient}
      />
    </>
  );
};

export { EmailHeader };
